import { useEffect, useState } from "react";

import { api, useAlCambiar, useDatos } from "../api";
import { Button, ContactLink, Drawer, Heading, IconButton, KeyValue, KeyValueList, Menu, Skeleton, Tabs, Text, Textarea } from "../ds";
import { destinos, fase as datosFase, FASES, puedeMover, siguiente } from "../fases";
import { fechaHora, hace, linkInstagram, linkWhatsapp, nombrePlantilla, sinProtocolo, telefono } from "../formato";
import { Enlace } from "../router";
import { useSesion } from "../sesion";
import type { DetalleLead, Fase, TarjetaPipeline } from "../tipos";
import { useAccion } from "./acciones";
import { Actividad } from "./Actividad";
import { FranjaDemo, PedirDemo } from "./Demo";
import s from "./DetallePipeline.module.css";

/* Las fases que se pueden deshacer desde el historial (`deshacerFase` en el servidor). */
const DESHACIBLES = ["info_enviada", "seguimiento", "pidio_mas", "reunion", "cliente", "no_interesa"];
/* Teclas con el detalle abierto. */
const TECLAS: Record<string, Fase> = { "1": "info_enviada", "2": "pidio_mas", "3": "reunion", "4": "cliente", s: "seguimiento", S: "seguimiento", n: "no_interesa", N: "no_interesa" };
const PROGRESO = FASES.filter((f) => f.fase !== "esperando" && f.fase !== "no_interesa");

type Pestana = "conversacion" | "datos" | "historial";

export function DetallePipeline({
  tarjeta,
  onCerrar,
  onMover,
  onNavegar,
  dialogoAbierto,
}: {
  tarjeta: TarjetaPipeline | null;
  onCerrar: () => void;
  onMover: (f: Fase) => void;
  onNavegar: (paso: 1 | -1) => void;
  dialogoAbierto: boolean;
}) {
  const { datos, recargar } = useDatos<DetalleLead>(tarjeta ? `/api/leads/${tarjeta.id}` : null);
  useAlCambiar(recargar);
  // Al pasar de una tarjeta a otra, la anterior no se muestra mientras carga la nueva.
  const d = datos?.id === tarjeta?.id ? datos : undefined;
  const [pestana, setPestana] = useState<Pestana>("conversacion");

  useEffect(() => {
    if (!tarjeta || dialogoAbierto) return;
    const alTeclear = (e: globalThis.KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (e.metaKey || e.ctrlKey || e.altKey || el?.closest("input, textarea, select, [contenteditable='true'], [role='menu']")) return;
      if (document.querySelector("dialog[open]")) return;
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        onNavegar(e.key === "ArrowDown" ? 1 : -1);
        return;
      }
      const f = TECLAS[e.key];
      if (f && puedeMover(tarjeta.fase, f)) {
        e.preventDefault();
        onMover(f);
      }
    };
    document.addEventListener("keydown", alTeclear);
    return () => document.removeEventListener("keydown", alTeclear);
  }, [tarjeta, dialogoAbierto, onMover, onNavegar]);

  if (!tarjeta) return null;
  const sig = siguiente(tarjeta.fase);
  const otras = destinos(tarjeta).filter((o) => o.fase !== sig && o.fase !== "no_interesa");
  const alcanzada = PROGRESO.findIndex((f) => f.fase === tarjeta.fase);

  return (
    <Drawer
      open
      label={`Detalle de ${tarjeta.nombre}`}
      onClose={onCerrar}
      header={
        <div className={s.cabecera}>
          <div className={s.titulo}>
            <Heading level={2} size="sm">
              {tarjeta.nombre}
            </Heading>
            <Text as="span" size="sm">
              {[tarjeta.zona, tarjeta.puntaje !== null ? `puntaje ${tarjeta.puntaje}` : null].filter(Boolean).join(" · ")}
            </Text>
          </div>
          <div className={s.botonesCabecera}>
            <IconButton icon="external-link" label="Abrir la ficha completa" variant="bordered" size="sm" as={Enlace} href={`/leads/${tarjeta.id}`} />
            <IconButton icon="x" label="Cerrar" variant="bordered" size="sm" onClick={onCerrar} />
          </div>
        </div>
      }
      footer={
        <>
          {sig ? (
            <Button icon="arrow-right" onClick={() => onMover(sig)}>
              Mover a {datosFase(sig).etiqueta}
            </Button>
          ) : null}
          {otras.length ? (
            <Menu
              label="Otra fase"
              trigger={
                <Button variant="outline" iconAfter="chevron-down">
                  Otra fase
                </Button>
              }
              items={otras.map((o) => ({ label: o.etiqueta, detail: Object.keys(TECLAS).find((k) => TECLAS[k] === o.fase), onSelect: () => onMover(o.fase) }))}
            />
          ) : null}
          {puedeMover(tarjeta.fase, "no_interesa") ? (
            <Button variant="danger-soft" onClick={() => onMover("no_interesa")}>
              No le interesa
            </Button>
          ) : null}
        </>
      }
    >
      <div className={s.fase}>
        {tarjeta.fase === "no_interesa" ? (
          <div className={s.progreso} data-cortado="true" aria-hidden="true">
            <span />
          </div>
        ) : (
          <div className={s.progreso} aria-hidden="true">
            {PROGRESO.map((f, i) => (
              <span key={f.fase} data-hecha={i <= alcanzada} />
            ))}
          </div>
        )}
        <Text size="sm">
          <strong className={tarjeta.fase === "no_interesa" ? s.no : s.actual}>{datosFase(tarjeta.fase).etiqueta}</strong>{" "}
          {tarjeta.fase === "esperando" ? `desde ${hace(tarjeta.enviadoEn)}` : hace(tarjeta.desde)} · {nombrePlantilla(tarjeta.plantilla)}
        </Text>
      </div>

      <Tabs
        value={pestana}
        onChange={(v) => setPestana(v as Pestana)}
        tabs={[
          { value: "conversacion", label: "Conversación" },
          { value: "datos", label: "Datos" },
          { value: "historial", label: "Historial", count: d?.eventos.length },
        ]}
      />
      {!d ? (
        <Skeleton lines={6} />
      ) : pestana === "conversacion" ? (
        <Conversacion d={d} tarjeta={tarjeta} onCambio={recargar} />
      ) : pestana === "datos" ? (
        <Datos d={d} tarjeta={tarjeta} />
      ) : (
        <Historial d={d} />
      )}
    </Drawer>
  );
}

function Conversacion({ d, tarjeta, onCambio }: { d: DetalleLead; tarjeta: TarjetaPipeline; onCambio: () => void }) {
  const { sesion } = useSesion();
  const chatwoot = sesion?.integraciones.chatwoot;
  const enviados = d.mensajes
    .filter((m) => m.canal === "whatsapp" && m.estado === "enviado" && m.enviadoEn)
    .map((m) => ({ clave: `m${m.id}`, cuando: m.enviadoEn!, nuestro: true, texto: m.cuerpo, etiqueta: m.plantilla ? `Plantilla · ${m.plantilla}` : null }));
  const respuestas = d.eventos
    .filter((e) => e.tipo === "respuesta")
    .map((e) => {
      const texto = typeof e.datos.texto === "string" && e.datos.texto.trim() ? e.datos.texto : typeof e.datos.nota === "string" ? e.datos.nota : "";
      return { clave: `e${e.id}`, cuando: e.creadoEn, nuestro: false, texto: texto || "Respondió (marcado a mano, sin texto).", etiqueta: e.datos.origen === "chatwoot" ? "Por Chatwoot" : null };
    });
  const burbujas = [...enviados, ...respuestas].sort((a, b) => a.cuando.localeCompare(b.cuando));
  const url = tarjeta.conversacion && chatwoot?.url && chatwoot.cuenta ? `${chatwoot.url}/app/accounts/${chatwoot.cuenta}/conversations/${tarjeta.conversacion}` : null;

  return (
    <div className={s.seccion}>
      {d.demo ? <FranjaDemo demo={d.demo} /> : null}
      <PedirDemo d={d} onCambio={onCambio} />
      <div className={s.chat}>
        {burbujas.map((b) => (
          <div key={b.clave} className={s.burbuja} data-nuestra={b.nuestro}>
            {b.etiqueta ? <span className={s.etiquetaBurbuja}>{b.etiqueta}</span> : null}
            <span className={s.textoBurbuja}>{b.texto}</span>
            <span className={s.hora}>{fechaHora(b.cuando)}</span>
          </div>
        ))}
        {url ? (
          <Button size="sm" variant="ghost" iconAfter="external-link" href={url} target="_blank" className={s.chatwoot}>
            Responder en Chatwoot
          </Button>
        ) : null}
      </div>
      <Nota d={d} />
    </div>
  );
}

function Nota({ d }: { d: DetalleLead }) {
  const { pendiente, ejecutar } = useAccion();
  const [nota, setNota] = useState(d.notas ?? "");
  useEffect(() => setNota(d.notas ?? ""), [d.id, d.notas]);
  const cambio = nota.trim() !== (d.notas ?? "").trim();
  return (
    <div className={s.nota}>
      <Textarea label="Nota" value={nota} onChange={(e) => setNota(e.target.value)} rows={3} placeholder="Qué dijo, qué le mandaste, cuándo volver a escribir." />
      {cambio ? (
        <div className={s.guardar}>
          <Button size="sm" variant="ghost" onClick={() => setNota(d.notas ?? "")}>
            Descartar
          </Button>
          <Button size="sm" icon="check" pending={pendiente === "nota"} onClick={() => ejecutar("nota", () => api(`/api/leads/${d.id}`, { metodo: "PATCH", cuerpo: { notas: nota } }), "Nota guardada")}>
            Guardar nota
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function Datos({ d, tarjeta }: { d: DetalleLead; tarjeta: TarjetaPipeline }) {
  return (
    <div className={s.seccion}>
      <KeyValueList tone="plain">
        {d.whatsapp ? (
          <KeyValue label="WhatsApp" mono>
            <ContactLink href={linkWhatsapp(d.whatsapp)}>{telefono(d.whatsapp)}</ContactLink>
          </KeyValue>
        ) : null}
        {d.email ? (
          <KeyValue label="Email">
            <ContactLink href={`mailto:${d.email}`}>{d.email}</ContactLink>
          </KeyValue>
        ) : null}
        {d.sitioWeb ? (
          <KeyValue label="Sitio">
            <ContactLink href={d.sitioWeb}>{sinProtocolo(d.sitioWeb)}</ContactLink>
          </KeyValue>
        ) : null}
        {d.instagram ? (
          <KeyValue label="Instagram">
            <ContactLink href={linkInstagram(d.instagram)}>@{d.instagram}</ContactLink>
          </KeyValue>
        ) : null}
      </KeyValueList>
      <KeyValueList tone="plain">
        <KeyValue label="Apertura" mono>
          {nombrePlantilla(tarjeta.plantilla)}
        </KeyValue>
        <KeyValue label="Enviado">
          {fechaHora(tarjeta.enviadoEn)}
          {tarjeta.responsable ? ` · por ${tarjeta.responsable}` : ""}
        </KeyValue>
        {tarjeta.reunion ? <KeyValue label="Reunión">{fechaHora(tarjeta.reunion)}</KeyValue> : null}
        <KeyValue label="Puntaje" mono>
          {d.puntaje ?? "–"} de 100
        </KeyValue>
      </KeyValueList>
      <Button size="sm" variant="ghost" iconAfter="arrow-right" as={Enlace} href={`/leads/${d.id}`} className={s.ficha}>
        Ver la ficha completa
      </Button>
    </div>
  );
}

function Historial({ d }: { d: DetalleLead }) {
  const { pendiente, ejecutar } = useAccion();
  const ultima = d.eventos.find((e) => DESHACIBLES.includes(e.tipo));
  return (
    <div className={s.seccion}>
      <Actividad detalle={d} />
      {ultima ? (
        <div className={s.deshacer}>
          <Text as="span" size="sm">
            ¿Se marcó una fase por error?
          </Text>
          <Button
            size="sm"
            variant="ghost"
            icon="rotate-ccw"
            pending={pendiente === "deshacer"}
            onClick={() => ejecutar("deshacer", () => api(`/api/leads/${d.id}/deshacer-fase`, { metodo: "POST" }), "Se deshizo la última fase")}
          >
            Deshacer la última
          </Button>
        </div>
      ) : null}
    </div>
  );
}
